import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSButton from "views/system-design/DSButton";
import DSCheckbox from "views/system-design/DSCheckBox";
import DSFormControlLabel from "views/system-design/DSFormControlLabel";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { versionTesterApi } from "../api/versionTesterApi";

interface StorySelectorProps {
  selectedStoryIds: string[];
  onSelectionChange: (ids: string[]) => void;
}

/**
 * StorySelector - Collapsible component for selecting stories to run during tests
 *
 * Features:
 * - Fetches stories from GET /api/stories
 * - Collapsible section with expand/collapse icon
 * - Shows count of selected stories
 * - Select All / Deselect All button
 * - Individual checkboxes for each story with name and description
 */
const StorySelector: React.FC<StorySelectorProps> = ({
  selectedStoryIds,
  onSelectionChange,
}) => {
  const [expanded, setExpanded] = useState(false);

  // Fetch stories from API
  const {
    data: stories,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["stories"],
    queryFn: () => versionTesterApi.getStories(),
    retry: 1,
  });

  const selectedCount = selectedStoryIds.length;
  const totalCount = stories?.length ?? 0;
  const allSelected = totalCount > 0 && selectedCount === totalCount;

  const handleToggleAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (allSelected) {
      onSelectionChange([]);
    } else {
      onSelectionChange(stories?.map((s) => s.id) ?? []);
    }
  };

  const handleToggleStory = (storyId: string) => {
    if (selectedStoryIds.includes(storyId)) {
      onSelectionChange(selectedStoryIds.filter((id) => id !== storyId));
    } else {
      onSelectionChange([...selectedStoryIds, storyId]);
    }
  };

  return (
    <DSBox sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {/* Collapsible header */}
      <DSBox
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          padding: 1,
          borderRadius: 1,
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ExpandMoreIcon
            sx={{
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
            }}
          />
          <DSTypography variant="subtitle1">
            User Stories ({selectedCount}/{totalCount} selected)
          </DSTypography>
        </DSBox>

        <DSButton
          variant="text"
          size="small"
          onClick={handleToggleAll}
          disabled={totalCount === 0}
        >
          {allSelected ? "Deselect All" : "Select All"}
        </DSButton>
      </DSBox>

      {/* Expanded content */}
      {expanded && (
        <DSBox
          sx={{
            paddingLeft: 4,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {isLoading && (
            <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <DSCircularProgress size={20} />
              <DSTypography variant="body2" color="text.secondary">
                Loading stories...
              </DSTypography>
            </DSBox>
          )}

          {isError && (
            <DSTypography variant="body2" color="error">
              Could not load stories
            </DSTypography>
          )}

          {!isLoading && !isError && stories && stories.length === 0 && (
            <DSTypography variant="body2" color="text.secondary">
              No stories available
            </DSTypography>
          )}

          {!isLoading && !isError && stories && stories.length > 0 && (
            <>
              {stories.map((story) => (
                <DSFormControlLabel
                  key={story.id}
                  control={
                    <DSCheckbox
                      checked={selectedStoryIds.includes(story.id)}
                      onChange={() => handleToggleStory(story.id)}
                    />
                  }
                  label={
                    <DSBox sx={{ display: "flex", flexDirection: "column" }}>
                      <DSTypography variant="body2" sx={{ fontWeight: "bold" }}>
                        {story.name}
                      </DSTypography>
                      <DSTypography variant="body2" color="text.secondary">
                        {story.description}
                      </DSTypography>
                    </DSBox>
                  }
                />
              ))}
            </>
          )}
        </DSBox>
      )}
    </DSBox>
  );
};

export default StorySelector;
